"use client";

import React, { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const methodologies = [];
function MethodologyBlock({
  item,
  index
}: {
  item: (typeof methodologies)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  return <div data-section-id="103688" data-index={index} className="relative w-full min-h-[80vh] flex items-center justify-end p-6 md:p-12 lg:p-24 overflow-hidden border-b border-border/10 last:border-0">
      {}
      <div className="absolute inset-0 z-0 bg-primary">
        <img src={item.image} alt={item.title} className="w-full h-full object-cover grayscale opacity-40 transition-transform duration-1000 hover:scale-105" />
        {}
        <div className="absolute inset-0 bg-primary/60 md:bg-gradient-to-r md:from-transparent md:via-primary/50 md:to-primary/90" />
      </div>

      {}
      <div ref={ref} className={`relative z-10 w-full max-w-2xl bg-primary/20 backdrop-blur-xl border border-primary-foreground/10 p-8 md:p-16 flex flex-col items-start md:items-end text-left md:text-right transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16"}`}>
        <span className="text-secondary font-mono text-sm tracking-widest mb-6 block uppercase">
          [{item.id}]
        </span>
        
        <h2 className="text-3xl md:text-5xl lg:text-6xl font-light tracking-tight text-primary-foreground mb-6 font-serif">
          {item.title}
        </h2>
        
        <p className="text-primary-foreground/70 text-lg md:text-xl leading-relaxed font-light max-w-lg mb-10">
          {item.description}
        </p>
        
        <Link to={item.link}>
          <Button variant="outline" className="border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground hover:text-primary rounded-none tracking-widest uppercase text-xs h-12 px-8 transition-colors duration-300 group">
            Learn More
            <ArrowRight className="ml-2 size-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Button>
        </Link>
      </div>
    </div>;
}
export default function PrecisionMethodologies() {
  return <section id="expert-guidance-at-every-step" className="w-full bg-background flex flex-col">
      {methodologies.map((item, i) => <MethodologyBlock key={item.id} item={item} index={i} />)}
    </section>;
}